import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid'
import { MdDeleteForever, MdEdit } from 'react-icons/md'
import useOdulOneriConfig from '../../hooks/useOdulOneriConfig'
import useOdulOneriCall from '../../hooks/useOdulOneriCall'
import {
  ODUL_FACILITIES,
  DEFAULT_DEPARTMENT_FACILITY,
  resolveDepartmentFacility,
  normalizeMemberFacilities,
} from '../../helper/odulOneriFacilities'

const EMPTY_DEPT = {
  id: '',
  name: '',
  facility: DEFAULT_DEPARTMENT_FACILITY,
  managerName: '',
  managerEmail: '',
  active: true,
}

const EMPTY_MEMBER = {
  fullName: '',
  title: '',
  email: '',
  role: 'uye',
  facilities: [],
  active: true,
}

const EMPTY_OPEX = {
  fullName: '',
  title: '',
  email: '',
  active: true,
}

/** İK config — bölüm/yönetici + kurul üyesi + OPEX kullanıcı CRUD */
const IKConfig = () => {
  const {
    getDepartments,
    addDepartment,
    updateDepartment,
    deleteDepartment,
    getBoardMembers,
    addBoardMember,
    updateBoardMember,
    deleteBoardMember,
    getOpexUsers,
    addOpexUser,
    updateOpexUser,
    deleteOpexUser,
  } = useOdulOneriConfig()
  const { wipeProcessData } = useOdulOneriCall()

  const [tab, setTab] = useState(0)
  const [wipeDialogOpen, setWipeDialogOpen] = useState(false)
  const [wipeBusy, setWipeBusy] = useState(false)
  const [wipeConfirmText, setWipeConfirmText] = useState('')
  const [loading, setLoading] = useState(false)
  const [departments, setDepartments] = useState([])
  const [members, setMembers] = useState([])
  const [opexUsers, setOpexUsers] = useState([])

  const [deptDialogOpen, setDeptDialogOpen] = useState(false)
  const [deptForm, setDeptForm] = useState(EMPTY_DEPT)
  const [editingDeptId, setEditingDeptId] = useState(null)
  const [deptSaving, setDeptSaving] = useState(false)

  const [memberDialogOpen, setMemberDialogOpen] = useState(false)
  const [memberForm, setMemberForm] = useState(EMPTY_MEMBER)
  const [editingMemberId, setEditingMemberId] = useState(null)
  const [memberSaving, setMemberSaving] = useState(false)

  const [opexDialogOpen, setOpexDialogOpen] = useState(false)
  const [opexForm, setOpexForm] = useState(EMPTY_OPEX)
  const [editingOpexId, setEditingOpexId] = useState(null)
  const [opexSaving, setOpexSaving] = useState(false)

  const loadDepartments = useCallback(async () => {
    setLoading(true)
    try {
      const list = await getDepartments()
      setDepartments(list)
    } finally {
      setLoading(false)
    }
  }, [getDepartments])

  const loadMembers = useCallback(async () => {
    setLoading(true)
    try {
      const list = await getBoardMembers()
      setMembers(list)
    } finally {
      setLoading(false)
    }
  }, [getBoardMembers])

  const loadOpexUsers = useCallback(async () => {
    setLoading(true)
    try {
      const list = await getOpexUsers()
      setOpexUsers(list)
    } finally {
      setLoading(false)
    }
  }, [getOpexUsers])

  useEffect(() => {
    let mounted = true
    ;(async () => {
      setLoading(true)
      try {
        if (tab === 0) {
          const list = await getDepartments()
          if (mounted) setDepartments(list)
        } else if (tab === 1) {
          const list = await getBoardMembers()
          if (mounted) setMembers(list)
        } else if (tab === 2) {
          const list = await getOpexUsers()
          if (mounted) setOpexUsers(list)
        }
      } finally {
        if (mounted) setLoading(false)
      }
    })()
    return () => {
      mounted = false
    }
  }, [tab, getDepartments, getBoardMembers, getOpexUsers])

  const openNewDept = () => {
    setEditingDeptId(null)
    setDeptForm(EMPTY_DEPT)
    setDeptDialogOpen(true)
  }

  const openEditDept = (row) => {
    setEditingDeptId(row.id)
    setDeptForm({
      id: row.id || '',
      name: row.name || '',
      facility: resolveDepartmentFacility(row.facility),
      managerName: row.managerName || '',
      managerEmail: row.managerEmail || '',
      active: row.active !== false,
    })
    setDeptDialogOpen(true)
  }

  const handleSaveDept = async (e) => {
    e.preventDefault()
    setDeptSaving(true)
    try {
      if (editingDeptId) {
        await updateDepartment(editingDeptId, deptForm)
      } else {
        await addDepartment(deptForm)
      }
      setDeptDialogOpen(false)
      await loadDepartments()
    } catch (_) {
      /* toast hook içinde */
    } finally {
      setDeptSaving(false)
    }
  }

  const handleDeleteDept = async (id) => {
    if (!window.confirm('Bu birimi silmek istediğinize emin misiniz?')) return
    try {
      await deleteDepartment(id)
      await loadDepartments()
    } catch (_) {
      /* toast */
    }
  }

  const openNewMember = () => {
    setEditingMemberId(null)
    setMemberForm(EMPTY_MEMBER)
    setMemberDialogOpen(true)
  }

  const openEditMember = (row) => {
    setEditingMemberId(row.id)
    setMemberForm({
      fullName: row.fullName || '',
      title: row.title || '',
      email: row.email || '',
      role: row.role === 'baskan' ? 'baskan' : 'uye',
      facilities: normalizeMemberFacilities(row.facilities),
      active: row.active !== false,
    })
    setMemberDialogOpen(true)
  }

  const handleSaveMember = async (e) => {
    e.preventDefault()
    setMemberSaving(true)
    try {
      if (editingMemberId) {
        await updateBoardMember(editingMemberId, memberForm)
      } else {
        await addBoardMember(memberForm)
      }
      setMemberDialogOpen(false)
      await loadMembers()
    } catch (_) {
      /* toast */
    } finally {
      setMemberSaving(false)
    }
  }

  const handleDeleteMember = async (id) => {
    if (!window.confirm('Bu kurul üyesini silmek istediğinize emin misiniz?')) return
    try {
      await deleteBoardMember(id)
      await loadMembers()
    } catch (_) {
      /* toast */
    }
  }

  const openNewOpex = () => {
    setEditingOpexId(null)
    setOpexForm(EMPTY_OPEX)
    setOpexDialogOpen(true)
  }

  const openEditOpex = (row) => {
    setEditingOpexId(row.id)
    setOpexForm({
      fullName: row.fullName || '',
      title: row.title || '',
      email: row.email || '',
      active: row.active !== false,
    })
    setOpexDialogOpen(true)
  }

  const handleSaveOpex = async (e) => {
    e.preventDefault()
    setOpexSaving(true)
    try {
      if (editingOpexId) {
        await updateOpexUser(editingOpexId, opexForm)
      } else {
        await addOpexUser(opexForm)
      }
      setOpexDialogOpen(false)
      await loadOpexUsers()
    } catch (_) {
      /* toast */
    } finally {
      setOpexSaving(false)
    }
  }

  const handleDeleteOpex = async (id) => {
    if (!window.confirm('Bu OPEX kullanıcısını silmek istediğinize emin misiniz?')) return
    try {
      await deleteOpexUser(id)
      await loadOpexUsers()
    } catch (_) {
      /* toast */
    }
  }

  const deptColumns = useMemo(
    () => [
      {
        field: 'facility',
        headerName: 'Tesis',
        minWidth: 130,
        flex: 0.7,
        headerAlign: 'center',
        align: 'center',
      },
      {
        field: 'name',
        headerName: 'Birim Adı',
        minWidth: 200,
        flex: 1.2,
        headerAlign: 'center',
        align: 'center',
      },
      {
        field: 'managerName',
        headerName: 'Yönetici Adı',
        minWidth: 160,
        flex: 1,
        headerAlign: 'center',
        align: 'center',
      },
      {
        field: 'managerEmail',
        headerName: 'Yönetici E-posta',
        minWidth: 200,
        flex: 1.2,
        headerAlign: 'center',
        align: 'center',
      },
      {
        field: 'active',
        headerName: 'Durum',
        minWidth: 110,
        flex: 0.6,
        headerAlign: 'center',
        align: 'center',
        valueGetter: (params) => (params.row.active !== false ? 'Aktif' : 'Pasif'),
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Aksiyon',
        minWidth: 110,
        getActions: (params) => [
          <GridActionsCellItem
            key="edit"
            icon={<MdEdit size={22} color="#E8C872" />}
            label="Düzenle"
            onClick={() => openEditDept(params.row)}
          />,
          <GridActionsCellItem
            key="delete"
            icon={<MdDeleteForever size={22} color="#C70039" />}
            label="Sil"
            onClick={() => handleDeleteDept(params.id)}
          />,
        ],
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const memberColumns = useMemo(
    () => [
      {
        field: 'fullName',
        headerName: 'Ad Soyad',
        minWidth: 180,
        flex: 1,
        headerAlign: 'center',
        align: 'center',
      },
      {
        field: 'title',
        headerName: 'Görev / Unvan',
        minWidth: 160,
        flex: 1,
        headerAlign: 'center',
        align: 'center',
      },
      {
        field: 'email',
        headerName: 'E-posta',
        minWidth: 200,
        flex: 1.2,
        headerAlign: 'center',
        align: 'center',
      },
      {
        field: 'facilities',
        headerName: 'Tesisler',
        minWidth: 160,
        flex: 1,
        headerAlign: 'center',
        align: 'center',
        valueGetter: (params) => {
          const list = normalizeMemberFacilities(params.row.facilities)
          return list.length ? list.join(', ') : 'Tümü'
        },
      },
      {
        field: 'role',
        headerName: 'Kurul Rolü',
        minWidth: 120,
        flex: 0.7,
        headerAlign: 'center',
        align: 'center',
        valueGetter: (params) =>
          params.row.role === 'baskan' ? 'Kurul Başkanı' : 'Üye',
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Aksiyon',
        minWidth: 110,
        getActions: (params) => [
          <GridActionsCellItem
            key="edit"
            icon={<MdEdit size={22} color="#E8C872" />}
            label="Düzenle"
            onClick={() => openEditMember(params.row)}
          />,
          <GridActionsCellItem
            key="delete"
            icon={<MdDeleteForever size={22} color="#C70039" />}
            label="Sil"
            onClick={() => handleDeleteMember(params.id)}
          />,
        ],
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const opexColumns = useMemo(
    () => [
      {
        field: 'fullName',
        headerName: 'Ad Soyad',
        minWidth: 180,
        flex: 1,
        headerAlign: 'center',
        align: 'center',
      },
      {
        field: 'title',
        headerName: 'Görev / Unvan',
        minWidth: 160,
        flex: 1,
        headerAlign: 'center',
        align: 'center',
      },
      {
        field: 'email',
        headerName: 'E-posta',
        minWidth: 220,
        flex: 1.2,
        headerAlign: 'center',
        align: 'center',
      },
      {
        field: 'active',
        headerName: 'Durum',
        minWidth: 110,
        flex: 0.6,
        headerAlign: 'center',
        align: 'center',
        valueGetter: (params) => (params.row.active !== false ? 'Aktif' : 'Pasif'),
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Aksiyon',
        minWidth: 110,
        getActions: (params) => [
          <GridActionsCellItem
            key="edit"
            icon={<MdEdit size={22} color="#E8C872" />}
            label="Düzenle"
            onClick={() => openEditOpex(params.row)}
          />,
          <GridActionsCellItem
            key="delete"
            icon={<MdDeleteForever size={22} color="#C70039" />}
            label="Sil"
            onClick={() => handleDeleteOpex(params.id)}
          />,
        ],
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const handleWipeProcessData = async () => {
    if (wipeConfirmText.trim().toUpperCase() !== 'TEMIZLE') return
    setWipeBusy(true)
    try {
      await wipeProcessData()
      setWipeDialogOpen(false)
      setWipeConfirmText('')
    } catch (_) {
      /* toast */
    } finally {
      setWipeBusy(false)
    }
  }

  return (
    <Box sx={{ width: '100%', maxWidth: 'none', px: { xs: 1.5, sm: 2 }, py: 2, mt: 10, boxSizing: 'border-box' }}>
      <Typography align="center" letterSpacing={3} fontWeight={700} mb={2}>
        Öneri Süreci Ayarları
      </Typography>

      <Alert severity="error" variant="outlined" sx={{ mb: 2 }}>
        <Typography variant="body2" fontWeight={600} gutterBottom>
          Süreç verisini temizle
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Tüm kayıtlar, tokenlar, uygulamalar ve kurul toplantıları silinir. Bölüm, kurul üye ve
          OPEX kullanıcı tanımları korunur.
        </Typography>
        <Button
          color="error"
          variant="contained"
          onClick={() => setWipeDialogOpen(true)}
          sx={{ textTransform: 'none' }}
        >
          Süreç verisini temizle…
        </Button>
      </Alert>

      <Tabs
        value={tab}
        onChange={(_, value) => setTab(value)}
        textColor="primary"
        indicatorColor="primary"
        sx={{ mb: 2 }}
      >
        <Tab label="Bölüm & Yönetici Yönetimi" sx={{ textTransform: 'none' }} />
        <Tab label="Kurul Üyesi Yönetimi" sx={{ textTransform: 'none' }} />
        <Tab label="OPEX Kullanıcı Yönetimi" sx={{ textTransform: 'none' }} />
      </Tabs>

      {tab === 0 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button variant="contained" onClick={openNewDept} sx={{ textTransform: 'none' }}>
              Yeni Birim/Yönetici Ekle
            </Button>
          </Box>
          <DataGrid
            loading={loading}
            rows={departments}
            columns={deptColumns}
            initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
            pageSizeOptions={[10, 25, 50]}
            disableRowSelectionOnClick
            sx={{ boxShadow: 4, minHeight: 420 }}
          />
        </Box>
      )}

      {tab === 1 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button variant="contained" onClick={openNewMember} sx={{ textTransform: 'none' }}>
              Yeni Kurul Üyesi Ekle
            </Button>
          </Box>
          <DataGrid
            loading={loading}
            rows={members}
            columns={memberColumns}
            initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
            pageSizeOptions={[10, 25, 50]}
            disableRowSelectionOnClick
            sx={{ boxShadow: 4, minHeight: 420 }}
          />
        </Box>
      )}

      {tab === 2 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button variant="contained" onClick={openNewOpex} sx={{ textTransform: 'none' }}>
              Yeni OPEX Kullanıcısı Ekle
            </Button>
          </Box>
          <DataGrid
            loading={loading}
            rows={opexUsers}
            columns={opexColumns}
            initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
            pageSizeOptions={[10, 25, 50]}
            disableRowSelectionOnClick
            sx={{ boxShadow: 4, minHeight: 420 }}
          />
        </Box>
      )}

      <Dialog
        open={deptDialogOpen}
        onClose={() => setDeptDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editingDeptId ? 'Birim / Yönetici Düzenle' : 'Yeni Birim / Yönetici'}
        </DialogTitle>
        <Box component="form" onSubmit={handleSaveDept}>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth required>
              <InputLabel id="dept-facility-label">Tesis</InputLabel>
              <Select
                labelId="dept-facility-label"
                label="Tesis"
                name="facility"
                value={deptForm.facility || DEFAULT_DEPARTMENT_FACILITY}
                onChange={(e) =>
                  setDeptForm((prev) => ({ ...prev, facility: e.target.value }))
                }
              >
                {ODUL_FACILITIES.map((facility) => (
                  <MenuItem key={facility} value={facility}>
                    {facility}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              required
              label="Birim Adı"
              name="name"
              value={deptForm.name}
              onChange={(e) => setDeptForm((prev) => ({ ...prev, name: e.target.value }))}
            />
            <TextField
              fullWidth
              label="Yönetici Adı Soyadı"
              name="managerName"
              value={deptForm.managerName}
              onChange={(e) => setDeptForm((prev) => ({ ...prev, managerName: e.target.value }))}
            />
            <TextField
              fullWidth
              required
              type="email"
              label="Yönetici E-posta Adresi"
              name="managerEmail"
              value={deptForm.managerEmail}
              onChange={(e) => setDeptForm((prev) => ({ ...prev, managerEmail: e.target.value }))}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={deptForm.active}
                  onChange={(e) =>
                    setDeptForm((prev) => ({ ...prev, active: e.target.checked }))
                  }
                />
              }
              label={deptForm.active ? 'Aktif' : 'Pasif'}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setDeptDialogOpen(false)} sx={{ textTransform: 'none' }}>
              İptal
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={deptSaving || !deptForm.name}
              sx={{ textTransform: 'none' }}
            >
              {deptSaving ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog
        open={memberDialogOpen}
        onClose={() => setMemberDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editingMemberId ? 'Kurul Üyesi Düzenle' : 'Yeni Kurul Üyesi'}
        </DialogTitle>
        <Box component="form" onSubmit={handleSaveMember}>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              required
              fullWidth
              label="Ad Soyad"
              name="fullName"
              value={memberForm.fullName}
              onChange={(e) =>
                setMemberForm((prev) => ({ ...prev, fullName: e.target.value }))
              }
            />
            <TextField
              fullWidth
              label="Görev / Unvan"
              name="title"
              value={memberForm.title}
              onChange={(e) => setMemberForm((prev) => ({ ...prev, title: e.target.value }))}
            />
            <TextField
              required
              fullWidth
              type="email"
              label="E-posta"
              name="email"
              value={memberForm.email}
              onChange={(e) => setMemberForm((prev) => ({ ...prev, email: e.target.value }))}
            />
            <FormControl fullWidth required>
              <InputLabel id="board-role-label">Kurul Rolü</InputLabel>
              <Select
                labelId="board-role-label"
                label="Kurul Rolü"
                name="role"
                value={memberForm.role}
                onChange={(e) => setMemberForm((prev) => ({ ...prev, role: e.target.value }))}
              >
                <MenuItem value="baskan">Kurul Başkanı</MenuItem>
                <MenuItem value="uye">Üye</MenuItem>
              </Select>
            </FormControl>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Tesis kapsamı
              </Typography>
              <FormGroup row>
                {ODUL_FACILITIES.map((facility) => (
                  <FormControlLabel
                    key={facility}
                    control={
                      <Checkbox
                        checked={(memberForm.facilities || []).includes(facility)}
                        onChange={(e) => {
                          const checked = e.target.checked
                          setMemberForm((prev) => {
                            const current = normalizeMemberFacilities(prev.facilities)
                            const next = checked
                              ? [...current, facility]
                              : current.filter((item) => item !== facility)
                            return {
                              ...prev,
                              facilities: normalizeMemberFacilities(next),
                            }
                          })
                        }}
                      />
                    }
                    label={facility}
                  />
                ))}
              </FormGroup>
              <FormHelperText>
                Boş = her iki tesis (normal üye). Fabrika müdürü için yalnızca kendi tesisini
                seçin.
              </FormHelperText>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={memberForm.active}
                  onChange={(e) =>
                    setMemberForm((prev) => ({ ...prev, active: e.target.checked }))
                  }
                />
              }
              label={memberForm.active ? 'Aktif' : 'Pasif'}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setMemberDialogOpen(false)} sx={{ textTransform: 'none' }}>
              İptal
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={memberSaving}
              sx={{ textTransform: 'none' }}
            >
              {memberSaving ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog
        open={opexDialogOpen}
        onClose={() => setOpexDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editingOpexId ? 'OPEX Kullanıcısı Düzenle' : 'Yeni OPEX Kullanıcısı'}
        </DialogTitle>
        <Box component="form" onSubmit={handleSaveOpex}>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              required
              fullWidth
              label="Ad Soyad"
              name="fullName"
              value={opexForm.fullName}
              onChange={(e) => setOpexForm((prev) => ({ ...prev, fullName: e.target.value }))}
            />
            <TextField
              fullWidth
              label="Görev / Unvan"
              name="title"
              value={opexForm.title}
              onChange={(e) => setOpexForm((prev) => ({ ...prev, title: e.target.value }))}
            />
            <TextField
              required
              fullWidth
              type="email"
              label="E-posta"
              name="email"
              value={opexForm.email}
              onChange={(e) => setOpexForm((prev) => ({ ...prev, email: e.target.value }))}
              helperText="Bildirim mailleri ve OPEX işlemleri bu adrese bağlanır."
            />
            <FormControlLabel
              control={
                <Switch
                  checked={opexForm.active}
                  onChange={(e) =>
                    setOpexForm((prev) => ({ ...prev, active: e.target.checked }))
                  }
                />
              }
              label={opexForm.active ? 'Aktif (OPEX yetkisi açık)' : 'Pasif (OPEX yetkisi kapalı)'}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setOpexDialogOpen(false)} sx={{ textTransform: 'none' }}>
              İptal
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={opexSaving}
              sx={{ textTransform: 'none' }}
            >
              {opexSaving ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={wipeDialogOpen} onClose={() => !wipeBusy && setWipeDialogOpen(false)}>
        <DialogTitle>Süreç verisini temizle</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 320 }}>
          <Typography variant="body2">
            Bu işlem geri alınamaz. Onaylamak için aşağıya <strong>TEMIZLE</strong> yazın.
          </Typography>
          <TextField
            fullWidth
            label="Onay"
            value={wipeConfirmText}
            onChange={(e) => setWipeConfirmText(e.target.value)}
            disabled={wipeBusy}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWipeDialogOpen(false)} disabled={wipeBusy} sx={{ textTransform: 'none' }}>
            İptal
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={wipeBusy || wipeConfirmText.trim().toUpperCase() !== 'TEMIZLE'}
            onClick={handleWipeProcessData}
            sx={{ textTransform: 'none' }}
          >
            {wipeBusy ? 'Siliniyor…' : 'Kalıcı olarak sil'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default IKConfig
